import { create } from 'zustand';

export interface AuthUser {
  id?: number | string;
  email?: string;
  first_name?: string;
  last_name?: string;
  fortnite_username?: string;
  fortnite_account_id?: string;
  phone?: string;
  balance_vbucks?: number;
  welcome_bonus_vbucks?: number;
  balance_ars?: number;
  balance_mxn?: number;
  balance_usd?: number;
  role?: string;
  is_verified?: boolean;
  is_email_verified?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  setUser: (user: AuthUser | null) => void;
  setToken: (token: string | null) => void;
  login: (user: AuthUser, token: string) => void;
  logout: () => void;
  updateUserBalance: (balance: number) => void;
  syncFromStorage: () => void;
  fetchMe: () => Promise<AuthUser | null>;
}

function getStoredUser(): AuthUser | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem('fn_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getStoredToken(): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return (
      window.localStorage.getItem('fn_token') ||
      window.localStorage.getItem('token') ||
      window.localStorage.getItem('auth_token') ||
      null
    );
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: getStoredUser(),
  token: getStoredToken(),
  isLoading: false,

  setUser: (user) => {
    try {
      if (user) {
        window.localStorage.setItem('fn_user', JSON.stringify(user));
      } else {
        window.localStorage.removeItem('fn_user');
      }
    } catch {}
    set({ user });
  },

  setToken: (token) => {
    try {
      if (token) {
        window.localStorage.setItem('fn_token', token);
      } else {
        window.localStorage.removeItem('fn_token');
        window.localStorage.removeItem('token');
        window.localStorage.removeItem('auth_token');
      }
    } catch {}
    set({ token });
  },

  login: (user, token) => {
    try {
      window.localStorage.setItem('fn_token', token);
      window.localStorage.setItem('fn_user', JSON.stringify(user));
    } catch {}
    set({ user, token });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('userChanged'));
      window.dispatchEvent(new Event('storage'));
    }
  },

  logout: () => {
    try {
      window.localStorage.removeItem('fn_token');
      window.localStorage.removeItem('token');
      window.localStorage.removeItem('auth_token');
      window.localStorage.removeItem('fn_user');
      window.localStorage.removeItem('fn_linked_accounts');
      window.localStorage.removeItem('gf_raffle_enrolled');
    } catch {}
    set({ user: null, token: null });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('userChanged'));
      window.dispatchEvent(new Event('storage'));
    }
  },

  updateUserBalance: (balance) => {
    const current = get().user;
    if (!current) return;
    const updated = { ...current, balance_vbucks: balance };
    get().setUser(updated);
  },

  syncFromStorage: () => {
    set({
      user: getStoredUser(),
      token: getStoredToken(),
    });
    get().fetchMe();
  },

  fetchMe: async () => {
    const token = get().token || getStoredToken();
    if (!token) return null;

    try {
      const res = await fetch('/api/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const freshUser: AuthUser = await res.json();
        get().setUser(freshUser);
        return freshUser;
      }

      if (res.status === 401) {
        // Token invalid or expired
        get().logout();
      }
    } catch (err) {
      console.warn('[AUTH] Error fetching /api/auth/me:', err);
    }
    return null;
  },
}));

// Cross-tab and window event listener
if (typeof window !== 'undefined') {
  window.addEventListener('storage', () => {
    useAuthStore.getState().syncFromStorage();
  });
  window.addEventListener('userChanged', () => {
    useAuthStore.getState().syncFromStorage();
  });
  window.addEventListener('focus', () => {
    useAuthStore.getState().fetchMe();
  });

  // Revalidate immediately on load if token exists
  if (getStoredToken()) {
    useAuthStore.getState().fetchMe();
  }
}
